import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { addDiagnosticLog } from '@/components/driver/DriverDiagnosticPanel';

const DRIVER_SESSION_TOKEN_KEY = 'driver_session_token';
const HEARTBEAT_INTERVAL = 60000; // 1 minute
const VALIDATION_TIMEOUT = 10000; // 10 seconds timeout for validation

// Structured logging helper
const logStep = (
  level: 'info' | 'warn' | 'error' | 'success',
  step: string,
  message: string,
  data?: unknown
) => {
  addDiagnosticLog(level, step, message, data);
};

interface DriverSessionState {
  isValid: boolean;
  isLoading: boolean;
  sessionToken: string | null;
  error: string | null;
}

interface SessionConflict {
  existingIp: string;
  lastActivity: string;
}

export const useDriverLoginSession = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState<DriverSessionState>({
    isValid: false,
    isLoading: true,
    sessionToken: null,
    error: null,
  });
  const [sessionConflict, setSessionConflict] = useState<SessionConflict | null>(null);
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const clientInfoRef = useRef<{ ip: string; userAgent: string } | null>(null);

  // Get client IP from edge function
  const getClientInfo = useCallback(async () => {
    if (clientInfoRef.current) {
      logStep('info', 'CLIENT_INFO', 'Usando informações em cache', clientInfoRef.current);
      return clientInfoRef.current;
    }
    
    logStep('info', 'CLIENT_INFO', 'Buscando informações do cliente via edge function...');
    
    try {
      const { data, error } = await supabase.functions.invoke('get-client-ip');
      
      if (error) {
        logStep('error', 'CLIENT_INFO', 'Erro ao obter IP do cliente', error);
        return { ip: '0.0.0.0', userAgent: navigator.userAgent };
      }
      
      clientInfoRef.current = {
        ip: data.ip,
        userAgent: data.user_agent || navigator.userAgent
      };
      
      logStep('success', 'CLIENT_INFO', 'Informações do cliente obtidas', clientInfoRef.current);
      return clientInfoRef.current;
    } catch (error) {
      logStep('error', 'CLIENT_INFO', 'Exceção ao obter informações do cliente', error);
      return { ip: '0.0.0.0', userAgent: navigator.userAgent };
    }
  }, []);

  // Create driver session
  const createSession = useCallback(async (forceLogout = false): Promise<boolean> => {
    logStep('info', 'SESSION_CREATE', `Iniciando criação de sessão (forceLogout: ${forceLogout})`);
    
    // Buscar usuário diretamente da sessão Supabase (já ativa após signIn),
    // em vez de depender do state React que pode estar stale no closure
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      logStep('error', 'SESSION_CREATE', 'Usuário não autenticado');
      setState(prev => ({ ...prev, error: 'Usuário não autenticado', isLoading: false }));
      return false;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      setSessionConflict(null);

      const clientInfo = await getClientInfo();

      logStep('info', 'SESSION_CREATE', 'Chamando RPC create_driver_session...', { ip: clientInfo.ip, forceLogout });
      const startTime = Date.now();
      
      const { data, error } = await supabase.rpc('create_driver_session', {
        p_ip_address: clientInfo.ip,
        p_user_agent: clientInfo.userAgent,
        p_device_fingerprint: null,
        p_force_logout: forceLogout
      });
      
      const duration = Date.now() - startTime;

      if (error) {
        logStep('error', 'SESSION_CREATE', `Erro ao criar sessão (${duration}ms)`, error);
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: 'Erro ao criar sessão' 
        }));
        return false;
      }

      const result = data as { 
        success: boolean; 
        error?: string; 
        session_token?: string;
        message?: string;
        existing_ip?: string;
        existing_last_activity?: string;
      };

      if (!result.success) {
        if (result.error === 'SESSION_CONFLICT') {
          logStep('warn', 'SESSION_CREATE', `Conflito de sessão detectado (${duration}ms)`, {
            existingIp: result.existing_ip,
            lastActivity: result.existing_last_activity
          });
          setSessionConflict({
            existingIp: result.existing_ip || 'Desconhecido',
            lastActivity: result.existing_last_activity || ''
          });
          setState(prev => ({ ...prev, isLoading: false }));
          return false;
        }
        
        logStep('error', 'SESSION_CREATE', `Falha ao criar sessão (${duration}ms)`, result);
        setState(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: result.error || 'Erro desconhecido' 
        }));
        return false;
      }

      // Session created successfully
      const token = result.session_token!;
      localStorage.setItem(DRIVER_SESSION_TOKEN_KEY, token);
      
      logStep('success', 'SESSION_CREATE', `Sessão criada com sucesso (${duration}ms)`, {
        tokenPreview: `${token.substring(0, 8)}...`
      });
      
      setState({
        isValid: true,
        isLoading: false,
        sessionToken: token,
        error: null
      });

      return true;
    } catch (error) {
      logStep('error', 'SESSION_CREATE', 'Exceção ao criar sessão', error);
      setState(prev => ({ 
        ...prev, 
        isLoading: false, 
        error: 'Erro ao criar sessão' 
      }));
      return false;
    }
  }, [getClientInfo]);

  // Validate existing session
  const validateSession = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem(DRIVER_SESSION_TOKEN_KEY);
    
    logStep('info', 'SESSION_VALIDATE', 'Iniciando validação de sessão', {
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 8)}...` : null,
      hasUser: !!user
    });
    
    if (!token || !user) {
      logStep('warn', 'SESSION_VALIDATE', 'Sem token ou usuário para validar', { hasToken: !!token, hasUser: !!user });
      setState({
        isValid: false,
        isLoading: false,
        sessionToken: null,
        error: null
      });
      return false;
    }

    try {
      logStep('info', 'SESSION_VALIDATE', 'Chamando RPC validate_driver_session...');
      const startTime = Date.now();
      
      const { data, error } = await supabase.rpc('validate_driver_session', {
        p_session_token: token,
        p_ip_address: null // Don't validate IP on heartbeat, only on creation
      });
      
      const duration = Date.now() - startTime;

      if (error) {
        logStep('error', 'SESSION_VALIDATE', `Erro ao validar sessão (${duration}ms)`, error);
        localStorage.removeItem(DRIVER_SESSION_TOKEN_KEY);
        setState({
          isValid: false,
          isLoading: false,
          sessionToken: null,
          error: 'Erro ao validar sessão'
        });
        return false;
      }

      const result = data as { valid: boolean; error?: string };

      if (!result.valid) {
        logStep('warn', 'SESSION_VALIDATE', `Sessão inválida (${duration}ms)`, result);
        localStorage.removeItem(DRIVER_SESSION_TOKEN_KEY);
        setState({
          isValid: false,
          isLoading: false,
          sessionToken: null,
          error: result.error || 'Sessão inválida'
        });
        return false;
      }

      logStep('success', 'SESSION_VALIDATE', `Sessão válida (${duration}ms)`);
      setState({
        isValid: true,
        isLoading: false,
        sessionToken: token,
        error: null
      });

      return true;
    } catch (error) {
      logStep('error', 'SESSION_VALIDATE', 'Exceção ao validar sessão', error);
      localStorage.removeItem(DRIVER_SESSION_TOKEN_KEY);
      setState({
        isValid: false,
        isLoading: false,
        sessionToken: null,
        error: 'Erro ao validar sessão'
      });
      return false;
    }
  }, [user]);

  // Invalidate session (logout)
  const invalidateSession = useCallback(async () => {
    const token = localStorage.getItem(DRIVER_SESSION_TOKEN_KEY);
    
    logStep('info', 'SESSION_INVALIDATE', 'Iniciando invalidação de sessão', { hasToken: !!token });

    try {
      if (token && user) {
        logStep('info', 'SESSION_INVALIDATE', 'Chamando RPC invalidate_driver_session...');
        await supabase.rpc('invalidate_driver_session', {
          p_session_token: token
        });
        logStep('success', 'SESSION_INVALIDATE', 'Sessão invalidada no servidor');
      }
    } catch (error) {
      logStep('error', 'SESSION_INVALIDATE', 'Erro ao invalidar sessão', error);
    } finally {
      localStorage.removeItem(DRIVER_SESSION_TOKEN_KEY);
      setState({
        isValid: false,
        isLoading: false,
        sessionToken: null,
        error: null
      });
      
      // Clear heartbeat
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      
      logStep('info', 'SESSION_INVALIDATE', 'Sessão local limpa');
    }
  }, [user]);

  // Force logout from other device
  const forceLogoutOtherDevice = useCallback(async (): Promise<boolean> => {
    logStep('info', 'FORCE_LOGOUT', 'Forçando logout de outro dispositivo');
    setSessionConflict(null);
    return createSession(true);
  }, [createSession]);

  // Clear conflict without forcing logout
  const cancelConflict = useCallback(() => {
    logStep('info', 'CANCEL_CONFLICT', 'Conflito de sessão cancelado pelo usuário');
    setSessionConflict(null);
  }, []);

  // Setup heartbeat
  useEffect(() => {
    if (state.isValid && state.sessionToken) {
      logStep('info', 'HEARTBEAT', `Iniciando heartbeat (intervalo: ${HEARTBEAT_INTERVAL}ms)`);
      
      heartbeatRef.current = setInterval(() => {
        logStep('info', 'HEARTBEAT', 'Executando heartbeat...');
        validateSession();
      }, HEARTBEAT_INTERVAL);

      return () => {
        if (heartbeatRef.current) {
          logStep('info', 'HEARTBEAT', 'Parando heartbeat');
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
      };
    }
  }, [state.isValid, state.sessionToken, validateSession]);

  // Initial validation on mount with timeout
  useEffect(() => {
    const hasToken = !!localStorage.getItem(DRIVER_SESSION_TOKEN_KEY);
    
    if (user && hasToken) {
      logStep('info', 'INIT', 'Iniciando validação inicial de sessão', { userId: user.id });
      
      const timeoutId = setTimeout(() => {
        logStep('error', 'INIT', `Timeout na validação (${VALIDATION_TIMEOUT}ms) - resetando estado`);
        localStorage.removeItem(DRIVER_SESSION_TOKEN_KEY);
        setState({
          isValid: false,
          isLoading: false,
          sessionToken: null,
          error: 'Timeout na validação da sessão'
        });
      }, VALIDATION_TIMEOUT);

      validateSession().finally(() => {
        clearTimeout(timeoutId);
      });

      return () => clearTimeout(timeoutId);
    } else if (user && !hasToken) {
      // Login fresco — sem token para validar, só desligar loading
      logStep('info', 'INIT', 'Usuário autenticado sem token de sessão driver - aguardando createSession');
      setState({
        isValid: false,
        isLoading: false,
        sessionToken: null,
        error: null
      });
    } else {
      logStep('info', 'INIT', 'Sem usuário autenticado - resetando estado');
      setState({
        isValid: false,
        isLoading: false,
        sessionToken: null,
        error: null
      });
    }
  }, [user, validateSession]);

  return {
    ...state,
    sessionConflict,
    createSession,
    validateSession,
    invalidateSession,
    forceLogoutOtherDevice,
    cancelConflict,
  };
};
